import "./DealTasksBoardBanner.css";

import { useContext, useMemo } from "react";

import { OrganisationTypeConstants } from "../../../../../../../constants";
import { useAuth } from "../../../../../../../useAuth";
import { getDateFromValue, isPastDateByDay, normalizeStage } from "../../../../../../../utils";
import { Button, Column, InformationBox, Row } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";
import { isInProgressStatus } from "../../utils";

const isCompletedStatus = (status?: string | null): boolean => normalizeStage(status).includes("complete");
const isPastDate = (value: unknown): boolean => {
  const parsedDate = getDateFromValue(value);

  if (!parsedDate) {
    return false;
  }

  return isPastDateByDay(parsedDate);
};

export const DealTasksBoardBanner = (): JSX.Element | undefined => {
  const { currentUserType } = useAuth();
  const { workflowSteps, dealStages, dealDates, currentDealStage, setShowCompleteStageModal, setShowEditDatesPanel } =
    useContext(TasksBoardContext);

  const currentStage = useMemo(
    () => dealStages.find((stage) => stage.key === currentDealStage),
    [currentDealStage, dealStages]
  );

  const currentStageEndDate = useMemo(() => {
    const currentStageDates = currentDealStage ? dealDates?.[currentDealStage] : undefined;
    return currentStageDates?.endDate ?? currentStageDates?.end;
  }, [currentDealStage, dealDates]);

  const handleEditDates = (): void => {
    setShowEditDatesPanel(true);
  };

  if (isCompletedStatus(currentStage?.status)) {
    return (
      <Row spacingV="ml">
        <Column span={12}>
          <InformationBox variant="success">
            <p className="body1">
              <b>You have completed this stage</b>
            </p>
          </InformationBox>
        </Column>
      </Row>
    );
  }

  if (isPastDate(currentStageEndDate)) {
    return (
      <Row spacingV="ml">
        <Column span={12}>
          <InformationBox
            variant="info"
            corners="rounded"
            onEdit={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? handleEditDates : undefined}
            editText="Edit dates"
          >
            <div className="DealTasksBoardBannerContent">
              <p className="body1">
                <b>Your deal is now overrunning the current date range</b>
              </p>
              <p className="body1">
                The current date is beyond the end date for this stage. To keep your timeline accurate, edit the stage
                and task deadlines.
              </p>
            </div>
          </InformationBox>
        </Column>
      </Row>
    );
  }

  // Incomplete phase with all steps completed
  if (isInProgressStatus(currentStage?.status) && workflowSteps?.every((step) => step.isComplete)) {
    return (
      <Row spacingV="ml">
        <Column span={12}>
          <InformationBox variant="secondarySuccess" corners="rounded">
            <div className="DealTasksBoardBannerActionContainer">
              <div className="DealTasksBoardBannerContent">
                <p className="body1">
                  <b>All steps in this stage have been completed</b>
                </p>
              </div>
              <div className="DealTasksBoardBannerAction">
                <Button
                  text="Complete stage"
                  textUnderline
                  variant="quinary"
                  fontWeight="light"
                  hasNoPadding
                  onClick={() => setShowCompleteStageModal(true)}
                />
              </div>
            </div>
          </InformationBox>
        </Column>
      </Row>
    );
  }

  return undefined;
};
